import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import TextField from '@mui/material/TextField';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import ClearIcon from '@mui/icons-material/Clear';
import IconButton from '@mui/material/IconButton';
import FormControl from '@mui/material/FormControl';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import SearchIcon from '@mui/icons-material/Search';
import RestartAltIcon from '@mui/icons-material/RestartAlt';

const Search = ({ visible, meta, setMeta }) => {
    const defaultInputRef = useRef(null);
    const profile = useSelector(state => state.profile.profile);
    const orders = useSelector(state => state.orders.createShipment);
    const [searchButtonDisabled, setSearchButtonDisabled] = useState(true);
    const [switchStatus, setSwitchStatus] = useState(false);
    const [userInteracted, setUserInteracted] = useState(false);
    
    // Local state tracking parameters initialization
    const [formValues, setFormValues] = useState({
        sku: "",
        quantity: "",
        order_id: "",
        tracking_number: "",
        customer_name: "",
        goods_out_note: "",
        reference_no: "",
        receiver_province: "",
        receiver_country_code: ""
    });
    
    const onInputChange = (e) => {
        const { name, value } = e.target;
        setFormValues({
            ...formValues,
            [name]: value
        });
    };

    // --- Form Checking Engine ---
    const checkFormHasValues = () => {
        const isSkuChanged = (formValues.sku || "") !== (meta?.sku || "");
        const isQuantityChanged = (formValues.quantity || "") !== (meta?.quantity || "");
        const isOrderIdChanged = (formValues.order_id || "") !== (meta?.order_id || "");
        const isTrackingNumberChanged = (formValues.tracking_number || "") !== (meta?.tracking_number || "");
        const isCustomerNameChanged = (formValues.customer_name || "") !== (meta?.customer_name || "");
        const isGoodsOutNoteChanged = (formValues.goods_out_note || "") !== (meta?.goods_out_note || "");
        const isReferenceNoChanged = (formValues.reference_no || "") !== (meta?.reference_no || "");
        const isReceiverProvinceChanged = (formValues.receiver_province || "") !== (meta?.receiver_province || "");
        const isReceiverCountryCodeChanged = (formValues.receiver_country_code || "") !== (meta?.receiver_country_code || "");

        if (
            isSkuChanged || isQuantityChanged || isOrderIdChanged ||
            isTrackingNumberChanged || isCustomerNameChanged || isGoodsOutNoteChanged ||
            isReferenceNoChanged || isReceiverProvinceChanged || isReceiverCountryCodeChanged
        ) {
            return false; // Enable search button
        }
        return true; // Keep disabled
    };

    const handleKeyUp = (event) => {
        if (event.keyCode === 13) {
            submitSearchValues();
        }
    };

    const submitSearchValues = () => {
        setMeta(prevState => ({
            ...prevState,
            [`sku`]: formValues["sku"],
            [`quantity`]: formValues["quantity"],
            [`order_id`]: formValues["order_id"],
            [`tracking_number`]: formValues["tracking_number"],
            [`customer_name`]: formValues["customer_name"],
            [`goods_out_note`]: formValues["goods_out_note"],
            [`reference_no`]: formValues["reference_no"],
            [`receiver_province`]: formValues["receiver_province"],
            [`receiver_country_code`]: formValues["receiver_country_code"],
            [`page`]: 1
        }));
    };

    const resetFields = () => {
        const clearedValues = {
            sku: "",
            quantity: "",
            order_id: "",
            tracking_number: "",
            customer_name: "",
            goods_out_note: "",
            reference_no: "",
            receiver_province: "",
            receiver_country_code: ""
        };
        setFormValues(clearedValues);
        
        setMeta(prevState => ({
            ...prevState,
            ...clearedValues,
            [`page`]: 1
        }));
    };

    const onInputClear = (name) => {
        setFormValues(prev => ({
            ...prev,
            [name]: ""
        }));
        setMeta(prevState => ({
            ...prevState,
            [name]: "",
            [`page`]: 1
        }));
    };

    const onChangeSwitch = (switchValue) => {
        setSwitchStatus(switchValue);
        setMeta({
            ...meta,
            [`auto_label_generate`]: switchValue
        });
    };

    // --- FIXED: TAB MOVEMENT RESET LOOP ---
    // Stringifies order_status array to detect tab/report changes instantly.
    // When order_status shifts on tab click, we explicitly hard-wipe the search form states.
    useEffect(() => {
        const clearedValues = {
            sku: "",
            quantity: "",
            order_id: "",
            tracking_number: "",
            customer_name: "",
            goods_out_note: "",
            reference_no: "",
            receiver_province: "",
            receiver_country_code: ""
        };
        
        setFormValues(clearedValues);
        
        setMeta(prevState => ({
            ...prevState,
            ...clearedValues
        }));
    }, [JSON.stringify(meta?.order_status)]); 

    useEffect(() => {
        setSearchButtonDisabled(checkFormHasValues());
    }, [formValues, meta]);

    useEffect(() => {
        if (profile && profile.auto_label_generate && profile.auto_label_generate === true) {
            setSwitchStatus(true);
        }
    }, [profile]);

    const handleFocus = (isDefault) => {
        if (!isDefault) {
            setUserInteracted(true);
        }
    };

    useEffect(() => {
        if (visible && defaultInputRef.current && !userInteracted) {
            defaultInputRef.current.focus();
        }
    }, [visible, userInteracted]);

    useEffect(() => {
        if (orders && orders.notification && orders.notification.messageContent
            && orders.notification.messageContent[0] === 'Shipment Created Successfully') {
            resetFields();
        }
    }, [orders]);

    // Reusable Enterprise UI Standard Form Control Styling Map
    const formFieldStyles = {
        width: '100%',
        '& .MuiOutlinedInput-root': {
            borderRadius: '10px',
            backgroundColor: '#FFFFFF',
            transition: 'all 0.2s ease-in-out',
            border: '1px solid #E2E8F0',
            '& fieldset': { border: 'none' },
            '&:hover': {
                borderColor: '#CBD5E1',
                boxShadow: '0 2px 8px rgba(0,0,0,0.02)'
            },
            '&.Mui-focused': {
                borderColor: '#1E293B',
                boxShadow: '0 0 0 3px rgba(30, 41, 59, 0.08)'
            }
        },
        '& .MuiInputLabel-outlined': {
            color: '#64748B',
            fontWeight: 500,
            transform: 'translate(14px, 10px) scale(1)',
            '&.Mui-focused, &.MuiInputLabel-shrink': {
                transform: 'translate(14px, -8px) scale(0.75)',
                color: '#1E293B',
                fontWeight: 600
            }
        },
        '& input': {
            padding: '10px 14px',
            fontSize: '14px',
            color: '#1E293B',
            fontWeight: 500
        }
    };

    return (
        <>
            {visible && (
                <Box 
                    className="filtercontainer"
                    sx={{ 
                        width: '100%',
                        bgcolor: '#F8FAFC',
                        borderRadius: '16px',
                        border: '1px solid #E2E8F0',
                        p: { xs: 2, md: 3 },
                        mb: 3,
                        boxShadow: '0 1px 3px rgba(0,0,0,0.02)'
                    }}
                >
                    <div className="filteraccordion" onKeyUp={handleKeyUp}>
                        <Grid container spacing={2.5}>
                            
                            {/* 1. Goods Out Note Input */}
                            <Grid item xs={12} sm={6} md={4} lg={3}>
                                <TextField
                                    fullWidth
                                    variant="outlined"
                                    sx={formFieldStyles}
                                    value={formValues.goods_out_note}
                                    name="goods_out_note"
                                    label="Scan Goods Out Note"
                                    size="small"
                                    onChange={onInputChange}
                                    onFocus={() => handleFocus(false)}
                                    InputProps={{
                                        endAdornment: formValues.goods_out_note ? (
                                            <IconButton size="small" onClick={() => onInputClear("goods_out_note")} edge="end">
                                                <ClearIcon fontSize="small" />
                                            </IconButton>
                                        ) : undefined
                                    }}
                                />
                            </Grid>

                            {/* 2. Order Number */}
                            <Grid item xs={12} sm={6} md={4} lg={3}>
                                <TextField
                                    fullWidth
                                    variant="outlined"
                                    sx={formFieldStyles}
                                    value={formValues.order_id}
                                    name="order_id"
                                    label="Order Number"
                                    size="small"
                                    onChange={onInputChange}
                                    inputRef={defaultInputRef}
                                    onFocus={() => handleFocus(true)}
                                    onKeyUp={(event) => {
                                        if (event.key === "Enter") submitSearchValues();
                                    }}
                                    InputProps={{
                                        endAdornment: formValues.order_id ? (
                                            <IconButton size="small" onClick={() => onInputClear("order_id")} edge="end">
                                                <ClearIcon fontSize="small" />
                                            </IconButton>
                                        ) : undefined
                                    }}
                                />
                            </Grid>

                            {/* 3. Tracking Number Input */}
                            <Grid item xs={12} sm={6} md={4} lg={3}>
                                <TextField
                                    fullWidth
                                    variant="outlined"
                                    sx={formFieldStyles}
                                    value={formValues.tracking_number}
                                    name="tracking_number"
                                    label="Tracking Number"
                                    size="small"
                                    onChange={onInputChange}
                                    onFocus={() => handleFocus(false)}
                                    InputProps={{
                                        endAdornment: formValues.tracking_number ? (
                                            <IconButton size="small" onClick={() => onInputClear("tracking_number")} edge="end">
                                                <ClearIcon fontSize="small" />
                                            </IconButton>
                                        ) : undefined
                                    }}
                                />
                            </Grid>

                            {/* 4. Customer Name Input */}
                            <Grid item xs={12} sm={6} md={4} lg={3}>
                                <TextField
                                    fullWidth
                                    variant="outlined"
                                    sx={formFieldStyles}
                                    value={formValues.customer_name}
                                    name="customer_name"
                                    label="Customer Name"
                                    size="small"
                                    onChange={onInputChange}
                                    onFocus={() => handleFocus(false)}
                                    InputProps={{
                                        endAdornment: formValues.customer_name ? (
                                            <IconButton size="small" onClick={() => onInputClear("customer_name")} edge="end">
                                                <ClearIcon fontSize="small" />
                                            </IconButton>
                                        ) : undefined
                                    }}
                                />
                            </Grid>

                            {/* 5. Reference Number Input */}
                            <Grid item xs={12} sm={6} md={4} lg={3}>
                                <TextField
                                    fullWidth
                                    variant="outlined"
                                    sx={formFieldStyles}
                                    value={formValues.reference_no}
                                    name="reference_no"
                                    label="Reference Number"
                                    size="small"
                                    onChange={onInputChange}
                                    onFocus={() => handleFocus(false)}
                                    InputProps={{
                                        endAdornment: formValues.reference_no ? (
                                            <IconButton size="small" onClick={() => onInputClear("reference_no")} edge="end">
                                                <ClearIcon fontSize="small" />
                                            </IconButton>
                                        ) : undefined
                                    }}
                                />
                            </Grid>

                            {/* 6. Receiver Province Code Input */}
                            <Grid item xs={12} sm={6} md={4} lg={3}>
                                <TextField
                                    fullWidth
                                    variant="outlined"
                                    sx={formFieldStyles}
                                    value={formValues.receiver_province}
                                    name="receiver_province"
                                    label="Receiver Province Code"
                                    size="small"
                                    onChange={onInputChange}
                                    onFocus={() => handleFocus(false)}
                                    InputProps={{
                                        endAdornment: formValues.receiver_province ? (
                                            <IconButton size="small" onClick={() => onInputClear("receiver_province")} edge="end">
                                                <ClearIcon fontSize="small" />
                                            </IconButton>
                                        ) : undefined
                                    }}
                                />
                            </Grid>

                            {/* 7. Receiver Country Code Input */}
                            <Grid item xs={12} sm={6} md={4} lg={3}>
                                <TextField
                                    fullWidth
                                    variant="outlined"
                                    sx={formFieldStyles}
                                    value={formValues.receiver_country_code}
                                    name="receiver_country_code"
                                    label="Receiver Country Code"
                                    size="small"
                                    onChange={onInputChange}
                                    onFocus={() => handleFocus(false)}
                                    InputProps={{
                                        endAdornment: formValues.receiver_country_code ? (
                                            <IconButton size="small" onClick={() => onInputClear("receiver_country_code")} edge="end">
                                                <ClearIcon fontSize="small" />
                                            </IconButton>
                                        ) : undefined
                                    }}
                                />
                            </Grid>

                            {/* 8. SKU Input */}
                            <Grid item xs={12} sm={6} md={4} lg={3}>
                                <TextField
                                    fullWidth
                                    variant="outlined"
                                    sx={formFieldStyles}
                                    value={formValues.sku}
                                    name="sku"
                                    label="SKU"
                                    size="small"
                                    onChange={onInputChange}
                                    onFocus={() => handleFocus(false)}
                                    InputProps={{
                                        endAdornment: formValues.sku ? (
                                            <IconButton size="small" onClick={() => onInputClear("sku")} edge="end">
                                                <ClearIcon fontSize="small" />
                                            </IconButton>
                                        ) : undefined
                                    }}
                                />
                            </Grid>

                            {/* 9. Quantity Input */}
                            <Grid item xs={12} sm={6} md={4} lg={3}>
                                <TextField
                                    fullWidth
                                    variant="outlined"
                                    sx={formFieldStyles}
                                    value={formValues.quantity}
                                    name="quantity"
                                    label="Quantity"
                                    size="small"
                                    onChange={onInputChange}
                                    onFocus={() => handleFocus(false)}
                                    InputProps={{
                                        endAdornment: formValues.quantity ? (
                                            <IconButton size="small" onClick={() => onInputClear("quantity")} edge="end">
                                                <ClearIcon fontSize="small" />
                                            </IconButton>
                                        ) : undefined
                                    }}
                                />
                            </Grid>

                            {/* 10. Auto Label Generate Switch Container Block */}
                            <Grid item xs={12} sm={6} md={4} lg={3} display="flex" alignItems="center">
                                <FormControlLabel
                                    sx={{ 
                                        userSelect: 'none',
                                        ml: 0.5,
                                        '& .MuiFormControlLabel-label': { 
                                            fontSize: '13.5px', 
                                            fontWeight: 600, 
                                            color: '#475569' 
                                        } 
                                    }}
                                    control={
                                        <Switch
                                            color="primary"
                                            checked={switchStatus}
                                            onChange={(e) => onChangeSwitch(e.target.checked)}
                                            size="small"
                                            name="auto_label_generate" 
                                        />
                                    }
                                    label="Auto Label Generate"
                                />
                            </Grid>

                        </Grid>

                        <Divider sx={{ my: 3, borderColor: '#E2E8F0', borderStyle: 'dashed' }} />

                        {/* Search Control Footer Action Row */}
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 1 }}>
                            <Button 
                                variant="outlined" 
                                size="medium"
                                startIcon={<RestartAltIcon />}
                                onClick={resetFields} 
                                disabled={searchButtonDisabled}
                                sx={{
                                    textTransform: 'none',
                                    borderRadius: '10px',
                                    fontWeight: 700,
                                    px: 3,
                                    borderColor: '#E2E8F0',
                                    color: '#475569',
                                    '&:hover': {
                                        borderColor: '#CBD5E1',
                                        backgroundColor: '#F1F5F9',
                                        color: '#1E293B'
                                    }
                                }}
                            >
                                Clear
                            </Button>
                            <Button 
                                variant="contained" 
                                size="medium"
                                startIcon={<SearchIcon />}
                                onClick={submitSearchValues} 
                                disabled={searchButtonDisabled}
                                sx={{
                                    textTransform: 'none',
                                    borderRadius: '10px',
                                    fontWeight: 700,
                                    px: 4,
                                    boxShadow: 'none',
                                    backgroundColor: '#1976d2', // Synced with corporate blue accent color theme
                                    color: '#FFFFFF',
                                    '&:hover': {
                                        backgroundColor: '#115293',
                                        boxShadow: 'none'
                                    },
                                    '&.Mui-disabled': {
                                        backgroundColor: '#E2E8F0',
                                        color: '#94A3B8'
                                    }
                                }}
                            >
                                Search
                            </Button>
                        </Box>
                    </div>
                </Box>
            )}
        </>
    );
};

export default Search;